import React, { useMemo } from 'react';

import { Loader, Select, SelectProps, useMantineTheme } from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { useLocalization } from 'context/localization-context';
import useEligibleDiscounts from 'hooks/discount/useEligibleDiscounts';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { EligibleDiscountList } from 'types';
import { formatDiscountValue } from 'utils/discount.helpers';

type DiscountsSelectProps = Omit<SelectProps, 'data' | 'rightSection' | 'value' | 'onChange'> & {
  athleteId: string | undefined;
  productId: string | undefined;
  value: EligibleDiscountList | null;
  onChange: (discount: EligibleDiscountList | null) => void;
  appliedTo?: 'invoice_item' | 'athlete_package';
};

const DiscountsSelect = ({
  athleteId,
  productId,
  value,
  onChange,
  appliedTo = 'athlete_package',
  ...selectProps
}: DiscountsSelectProps): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization();
  const { trackDiscountApplied } = useStaffTracking();
  const { data, isLoading } = useEligibleDiscounts({ productId, athleteId });

  const eligibleDiscounts = useMemo<EligibleDiscountList[]>(() => {
    return data?.data ?? [];
  }, [data]);

  const options = useMemo<SelectItem[]>(() => {
    return eligibleDiscounts.map(d => ({
      value: d.discountId,
      label: d.isRedeemedByAthlete
        ? `${d.name} ${formatDiscountValue(d, formatCurrency)} (already used)`
        : `${d.name} ${formatDiscountValue(d, formatCurrency)}`,
      code: d.code,
      disabled: d.isRedeemedByAthlete,
    }));
  }, [eligibleDiscounts, formatCurrency]);

  const handleChange = (id: string | null): void => {
    if (!id) {
      onChange(null);
      return;
    }
    const selected = eligibleDiscounts.find(d => d.discountId === id);
    if (selected?.isRedeemedByAthlete) return;
    onChange(selected ?? null);
    if (selected) {
      trackDiscountApplied(selected.discountId, selected.name, selected.valueType, appliedTo);
    }
  };

  return (
    <Select
      {...selectProps}
      value={value?.discountId ?? null}
      onChange={handleChange}
      data={options}
      searchable
      clearable
      nothingFound='No discounts found'
      maxDropdownHeight={200}
      filter={(searchValue, item) => {
        const term = searchValue.toLowerCase().trim();
        return (
          (item.label?.toLowerCase().includes(term) ?? false) ||
          (item.code?.toLowerCase().includes(term) ?? false)
        );
      }}
      rightSection={isLoading ? <Loader size={theme.fontSizes.md} /> : undefined}
    />
  );
};

export default DiscountsSelect;
